'use client';

import React, { useState, useEffect, useRef, useCallback, Suspense } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
// import { Card } from '@/app/components/card';
import { Search, Loader2, Filter } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { loadCharacterChatHistoryAction, loadPaginatedPublicAvatarsActionOptimized } from '../../lib/actions';
import { useTags } from '@/app/lib/avatar-tags/useTags';
import type { Character } from '@/app/lib/types/character';
import { useNavbar } from '@/app/lib/contexts/NavbarContext';
import { useUserAppData } from '@/app/user-and-session/user-app-data/useUserAppData';
import { useAuth } from '@/app/user-and-session/session-auth/hooks/useAuth';
import { getPresignedUrl } from '@/app/lib/actions/s3';
import { AdvancedImage } from '@cloudinary/react';
import { createCloudinaryImages, createCloudinaryImageSync, isCloudinaryReady, preloadCloudinary, createCloudinaryImagesWithPreload } from '@/app/lib/cloudinary/cloudinary-image-utils';
import type { CloudinaryPreloadResult } from '@/app/lib/cloudinary/cloudinary-preload';
import { PWAInstallBanner } from '@/app/components/pwa/pwa-install-banner';
import { useLocale } from '@/app/user-and-session/languages/i18n';
import type { Locale } from '@/app/user-and-session/languages/i18n/config';

interface MobileDiscoverCharactersProps {
  initialAvatars: {
    success: boolean;
    avatars: Character[] | null;
    message: string;
    hasMore: boolean;
  };
  cloudinaryPreload: CloudinaryPreloadResult | null;
}

function LoadingState() {
  const t = useTranslations('discover');
  return (
    <div className="flex flex-col items-center gap-6 p-6">
      <Loader2 className="w-12 h-12 animate-spin text-white" />
      <p className="text-white">{t('loadingCharacters')}</p>
    </div>
  );
}

// === Layout Configurable Parameters ===
const MIN_CARD_WIDTH = 140; // px (smaller for mobile)
const MIN_GAP = 2;          // px
const MAX_GAP = 6;          // px
// === Layout Configurable Parameters ===

// Add SearchStatus type
const SearchStatus = {
  Idle: 'Idle',
  Loading: 'Loading',
  Loaded: 'Loaded',
  NoResults: 'NoResults',
} as const;
type SearchStatusType = typeof SearchStatus[keyof typeof SearchStatus];

export default function MobileDiscoverCharacters({ initialAvatars, cloudinaryPreload }: MobileDiscoverCharactersProps) {
  const router = useRouter();
  const t = useTranslations('discover');
  const tTags = useTranslations('tags');
  const { isCollapsed: navbarCollapsed } = useNavbar();
  const { userEmail } = useAuth();
  const [userAppDataState] = useUserAppData(userEmail || '');
  const { locale } = useLocale();

  // Data state  
  const [avatars, setAvatars] = useState<Character[]>(initialAvatars.avatars || []);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState<boolean>(initialAvatars.hasMore || true);
  const [offset, setOffset] = useState(initialAvatars.avatars?.length || 0);
  
  // Cloudinary images state - maps avatar_id to cloudinary image object
  const [cloudinaryImages, setCloudinaryImages] = useState<Record<string, any>>({});

  // UI state
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filter panel state
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const filterPanelRef = useRef<HTMLDivElement>(null);

  // Tab and filter state
  const [activeMainTab, setActiveMainTab] = useState('trending');
  const [genderFilter, setGenderFilter] = useState<'all' | 'male' | 'female' | 'non-binary'>('all');
  const [includeFiltered, setIncludeFiltered] = useState<boolean>(false);

  // Tags state
  const { tags: allTagsList, isLoading: isLoadingTags } = useTags();
  const [selectedTags, setSelectedTags] = useState<number[]>([]);

  // Add searchStatus state
  const [searchStatus, setSearchStatus] = useState<SearchStatusType>(SearchStatus.Idle);
  
  // Add search focus state
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  // Initialize Cloudinary images using preloaded data
  useEffect(() => {
    const initializeCloudinaryImages = () => {
      // Only initialize if we have valid preload data
      if (!cloudinaryPreload?.cloudName || !cloudinaryPreload?.processedAvatars?.length) {
        console.warn('Cloudinary preload data is missing or invalid');
        return;
      }

      // Use preloaded data for instant initialization
      const cloudinaryImages = createCloudinaryImagesWithPreload(
        cloudinaryPreload.cloudName,
        cloudinaryPreload.processedAvatars
      );

      setCloudinaryImages(cloudinaryImages);
      console.log(`Initialized ${Object.keys(cloudinaryImages).length} Cloudinary images`);
    };

    initializeCloudinaryImages();
  }, [cloudinaryPreload]); // Re-run when cloudinaryPreload changes

  // Fast synchronous processing for new avatars (after Cloudinary is initialized)
  const processNewAvatarsSync = useCallback((newAvatars: Character[]) => {
    if (!isCloudinaryReady() || newAvatars.length === 0) return;

    const newCloudinaryImages: Record<string, any> = {};
    
    newAvatars.forEach(avatar => {
      const publicId = avatar.metadata.coverImgPresignedUrl || avatar.metadata.presignedUrl;
      if (publicId) {
        const cloudinaryImg = createCloudinaryImageSync(publicId);
        if (cloudinaryImg) {
          newCloudinaryImages[avatar.metadata.avatar_id] = cloudinaryImg;
        }
      }
    });

    if (Object.keys(newCloudinaryImages).length > 0) {
      setCloudinaryImages(prev => ({
        ...prev,
        ...newCloudinaryImages
      }));
    }
  }, []);

  // Store image keys and character metadata in session storage whenever avatars are loaded
  useEffect(() => {
    const cacheAvatarData = async () => {
      const userEmail = userAppDataState.userEmail;
      const userId = userAppDataState.userId;
      
      // Process all avatars in parallel using Promise.all with map
      await Promise.all(avatars.map(async (avatar) => {
        // Cache character metadata if not already cached
        if (avatar.metadata.avatar_id) {
          if (!sessionStorage.getItem(`chat_${avatar.metadata.avatar_id}_metadata`)) {
            try {
              const metadataJson = JSON.stringify(avatar.metadata);
              sessionStorage.setItem(`chat_${avatar.metadata.avatar_id}_metadata`, metadataJson);
            } catch (error) {
              console.error('Failed to cache character metadata:', error);
            }
          }
        }
        var chatHistoryResult = null;
        // Cache Chat History for authenticated users if not already cached
        if (false && !sessionStorage.getItem(`chat_${avatar.metadata.avatar_id}_${userId}_chatHistory`)) {
          try {
            chatHistoryResult = await loadCharacterChatHistoryAction(avatar.metadata.avatar_id, userEmail);
            if (chatHistoryResult.success && chatHistoryResult.chatHistory?.hasHistory) {
              sessionStorage.setItem(`chat_${avatar.metadata.avatar_id}_${userId}_chatHistory`, JSON.stringify(chatHistoryResult.chatHistory));
            }
            else if (chatHistoryResult.success && !chatHistoryResult.chatHistory?.hasHistory) {
              const emptyChatHistory = {
                hasHistory: false,
                messages: [],
              };
              sessionStorage.setItem(`chat_${avatar.metadata.avatar_id}_${userId}_chatHistory`, JSON.stringify(emptyChatHistory));
            }
          } catch (error) {
            console.error('Failed to cache chat history:', error);
          }
        }
        
        // Get last image, then get s3 image key, then get presigned URL
        // if (!sessionStorage.getItem(`image_${avatar.metadata.avatar_id}_imageKey`)) {
        //   if (chatHistoryResult) {
        //     var imageKey = null;
        //     const chatHistory = chatHistoryResult?.chatHistory;
        //     try {
        //       if (chatHistory?.messages && chatHistory.messages.length > 0) {
        //         // Find the last message with an image (search from the end)
        //         for (let i = chatHistory.messages.length - 1; i >= 0; i--) {
        //           const message = chatHistory.messages[i];
        //           if (message.imageUrl && message.imageUrl.trim() !== '') {
        //             imageKey = message.imageUrl;
        //             break;
        //           }
        //         }
        //       }
        //     } catch (error) {
        //       console.error('Failed to parse cached chat history:', error);
        //     }
        //   }
        //   // If no image found in chat history, use metadata image
        //   if (!imageKey && avatar.metadata.image_uri) {
        //     imageKey = avatar.metadata.image_uri;
        //   }
        //   // Get presigned URL and store in local storage
        //   if (imageKey) {
        //     try {
        //       const presignedUrl = (await getPresignedUrl(imageKey)).presignedUrl;
        //       // Store presigned URL in local storage
        //       sessionStorage.setItem(`image_${avatar.metadata.avatar_id}_imageKey`, imageKey);
        //     } catch (error) {
        //       console.error('Failed to get presigned URL for image:', error);
        //     }  
        //   }
        // }
      }));
    };
    
    cacheAvatarData();
  }, [avatars]);

  // Card width state for aspect ratio
  const [cardWidth, setCardWidth] = useState(MIN_CARD_WIDTH);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!gridRef.current) return;
    const updateCardWidth = () => {
      if (!gridRef.current) return;
      // Find the first card element
      const firstCard = gridRef.current.querySelector('.discover-card');
      if (firstCard) {
        setCardWidth((firstCard as HTMLElement).offsetWidth);
      }
    };
    updateCardWidth();
    const resizeObserver = new ResizeObserver(updateCardWidth);
    resizeObserver.observe(gridRef.current);
    return () => resizeObserver.disconnect();
  }, []);



  // Handle filter panel close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterPanelRef.current && !filterPanelRef.current.contains(event.target as Node)) {
        setShowFilterPanel(false);
      }
    };

    if (showFilterPanel) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFilterPanel]);

  // Prevent body scroll when filter panel is open
  useEffect(() => {
    if (showFilterPanel) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showFilterPanel]);

  // Observer for infinite scrolling
  const observer = useRef<IntersectionObserver | null>(null);
  const lastAvatarElementRef = useCallback((node: HTMLDivElement | null) => {
    if (isLoading) return;
    if (observer.current) observer.current.disconnect();
    
    observer.current = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting && hasMore) {
        loadMoreAvatars();
      }
    });
    
    if (node) observer.current.observe(node);
  }, [isLoading, hasMore]);

  // Unified reload function that handles both locale changes and filter toggles
  const reloadInitialAvatars = useCallback(async (options?: { localeOverride?: Locale; includeFilteredOverride?: boolean }) => {
    setIsLoading(true);
    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const currentLocale = options?.localeOverride ?? locale;
      const currentIncludeFiltered = options?.includeFilteredOverride ?? includeFiltered;
      
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 8, searchTerm, sortBy, genderFilter, selectedTags, currentIncludeFiltered, currentLocale);
      
      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));
        
        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        if (searchTerm.trim()) {
          setSearchStatus(processedAvatars.length === 0 ? SearchStatus.NoResults : SearchStatus.Loaded);
        } else {
          setSearchStatus(SearchStatus.Idle);
        }
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      } else {
        setAvatars([]);
        setHasMore(false);
        setSearchStatus(SearchStatus.NoResults);
      }
    } catch (error) {
      console.error('Error reloading avatars:', error);
      setAvatars([]);
      setHasMore(false);
      setSearchStatus(SearchStatus.NoResults);
    } finally {
      setIsLoading(false);
    }
  }, [activeMainTab, searchTerm, genderFilter, selectedTags, locale, includeFiltered]);

  // Reload avatars when locale changes
  useEffect(() => {
    reloadInitialAvatars({ localeOverride: locale });
  }, [locale, reloadInitialAvatars]);

  // Load more avatars
  const loadMoreAvatars = async () => {
    if (isLoading || !hasMore) return;

    setIsLoading(true);
    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const result = await loadPaginatedPublicAvatarsActionOptimized(offset, 8, '', sortBy, genderFilter, selectedTags, includeFiltered, locale);

      if (result.success && result.avatars && result.avatars.length > 0) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));

        setAvatars(prev => [...prev, ...processedAvatars]);
        setOffset(prev => prev + processedAvatars.length);
        setHasMore(result.hasMore || false);
        // Process new avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more avatars:', error);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  };

  // Search functionality
  const handleSearch = () => {
    setSearchStatus(SearchStatus.Loading);
    handleTabChange(activeMainTab, searchTerm);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    setIsSearchFocused(false);
  };

  // Tab change handler
  const handleTabChange = async (tab: string, search?: string) => {
    setActiveMainTab(tab);
    setIsLoading(true);
    setSearchStatus(SearchStatus.Loading);

    try {
      const sortBy = tab === 'trending' ? 'score' : 'time';
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 8, search || '', sortBy, genderFilter, selectedTags, includeFiltered, locale);

      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));

        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        setSearchStatus(processedAvatars.length === 0 ? SearchStatus.NoResults : SearchStatus.Loaded);
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      } else {
        setAvatars([]);
        setHasMore(false);
        setSearchStatus(SearchStatus.NoResults);
      }
    } catch (error) {
      console.error('Error changing tab:', error);
      setAvatars([]);
      setHasMore(false);
      setSearchStatus(SearchStatus.NoResults);
    } finally {
      setIsLoading(false);
    }
  };

  // Gender filter handler
  const handleGenderFilterChange = async (newGender: typeof genderFilter, overrideIncludeFiltered?: boolean) => {
    setGenderFilter(newGender);
    setIsLoading(true);

    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const currentIncludeFiltered = overrideIncludeFiltered ?? includeFiltered;
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 8, searchTerm, sortBy, newGender, selectedTags, currentIncludeFiltered);

      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));

        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      }
    } catch (error) {
      console.error('Error filtering by gender:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Tag filter handler
  const handleTagFilterChange = async (tagId: number) => {
    const newSelectedTags = selectedTags.includes(tagId)
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];

    setSelectedTags(newSelectedTags);
    setIsLoading(true);

    try {
      const sortBy = activeMainTab === 'trending' ? 'score' : 'time';
      const result = await loadPaginatedPublicAvatarsActionOptimized(0, 8, searchTerm, sortBy, genderFilter, newSelectedTags, includeFiltered);

      if (result.success && result.avatars) {
        const processedAvatars = result.avatars.map(avatar => ({
          ...avatar,
          metadata: {
            ...avatar.metadata,
            create_time: new Date(avatar.metadata.create_time)
          }
        }));

        setAvatars(processedAvatars);
        setOffset(processedAvatars.length);
        setHasMore(result.hasMore || false);
        // Process avatar images for Cloudinary (fast sync)
        processNewAvatarsSync(processedAvatars);
      }
    } catch (error) {
      console.error('Error filtering by tags:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle icon click to refresh
  const handleIconClick = async () => {
    router.push('/discover');
    setSearchTerm('');
    handleSearch();
  };


  if (!initialAvatars.success || !initialAvatars.avatars) {
    return (
      <div className="flex flex-col items-center gap-6 p-6">
        <h2 className="text-xl font-semibold mb-4 text-white">{t('exploreAvatars')}</h2>
        <div className="p-4 bg-red-100 text-red-700 rounded-lg">
          {initialAvatars.message || t('failedToLoadAvatars')}
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<LoadingState />}>
      <div className="bg-[#121214] h-full w-full flex flex-col overflow-x-hidden">
        {/* Compact Header Section */}
        <header className="relative bg-[#121214] flex items-center justify-between z-10 w-full h-14 px-2" style={{ minHeight: 56 }}>
          <div className="flex items-center gap-2 w-full">
            {/* Beta Icon - Hidden when search is focused */}
            {!isSearchFocused && (
              <div className="flex items-center justify-start transition-all duration-300 ease-in-out flex-shrink-0 ml-2" style={{ height: 44 }}>
                <button
                  onClick={handleIconClick}
                  className="h-full p-0 m-0 rounded-full hover:opacity-80 transition-opacity duration-200 cursor-pointer bg-transparent border-none flex-shrink-0"
                  style={{ height: '100%' }}
                >
                  <img 
                    src="/icon_beta_cropped.png" 
                    alt="Beta" 
                    className="object-contain h-full w-auto"
                    style={{ display: 'block' }}
                  />
                </button>
              </div>
            )}

            {/* Centered Search - Grows to full width when focused */}
            <div className={`${isSearchFocused ? 'flex-1 mx-0' : 'flex-1 mx-2'} min-w-0`}>
              <div className="relative">
                <div className="flex h-9 items-center rounded-full bg-[#2a2a2e] border border-[#ffffff1a] px-4 relative">
                  {/* Clickable search icon area */}
                  <button
                    onClick={() => searchInputRef.current?.focus()}
                    className="w-4 h-4 text-gray-400 mr-3 cursor-pointer bg-transparent border-none p-0 m-0 flex items-center justify-center"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                  
                  {/* Search input */}
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleSearchFocus}
                    onBlur={handleSearchBlur}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleSearch();
                      }
                    }}
                    placeholder={t('searchPlaceholder')}
                    className="flex-1 h-full bg-transparent text-white text-sm outline-none placeholder:text-gray-400 font-['Montserrat',Helvetica] min-w-0 mr-2 cursor-text"
                  />
                  
                  {/* Filter button - separate layer with guaranteed space */}
                  <button
                    onClick={() => setShowFilterPanel(true)}
                    className="w-5 h-5 text-gray-500 hover:text-white transition-colors duration-200 cursor-pointer bg-transparent border-none p-0 m-0 flex items-center justify-center flex-shrink-0"
                  >
                    <Filter className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Filter Panel Overlay */}
        {showFilterPanel && (
          <div className="absolute inset-0 z-50 flex items-end">
            {/* Dark overlay */}
            <div 
              className="absolute inset-0 bg-black/50 backdrop-blur-sm"
              onClick={() => setShowFilterPanel(false)}
            />
            
            {/* Bottom-up panel */}
            <div
              ref={filterPanelRef}
              className="relative w-full bg-[#1a1a1e] rounded-t-[20px] p-6 max-h-[70vh] overflow-y-auto"
              style={{
                animation: 'slideUp 0.3s ease-out'
              }}
            >
              {/* Handle bar */}
              <div className="flex justify-center mb-6">
                <div className="w-12 h-1 bg-gray-600 rounded-full"></div>
              </div>
              
              {/* Panel content */}
              <div className="space-y-8">

                {/* Gender Filter Row */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">{t('gender')}</h4>
                  <div className="flex gap-2">
                    {["all", "male", "female", "non-binary"].map((gender) => (
                      <button
                        key={gender}
                        onClick={() => handleGenderFilterChange(gender as typeof genderFilter)}
                        className={`flex-1 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 font-['Montserrat',Helvetica] capitalize ${
                          genderFilter === gender
                            ? "bg-[#ffffff1a] text-white shadow-[0px_0px_8px_#ffffff40] border border-white/80 outline outline-2 outline-white/80"
                            : "bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border border-transparent"
                        }`}
                      >
                        {gender === 'non-binary' ? t('nonBinary') : t(gender)}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Sort By Row */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">{t('sortBy')}</h4>
                  <div className="flex gap-2">
                    {["trending", "latest"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => handleTabChange(tab)}
                        className={`flex-1 px-4 py-3 rounded-xl font-medium text-sm transition-all duration-200 font-['Montserrat',Helvetica] capitalize ${
                          activeMainTab === tab
                            ? "bg-[#00000033] text-white shadow-[0px_0px_10px_#ffffff40] border border-white/80 outline outline-2 outline-white/80"
                            : "bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border border-transparent"
                        }`}
                      >
                        {tab === 'latest' ? t('latest') : t('trending')}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Filtered Content Toggle */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">{t('filtered')}</h4>
                  <div className="flex items-center gap-2">
                    {/* <span className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">
                      {includeFiltered ? 'Show Filtered Cards' : 'Hide Filtered Cards'}
                    </span> */}
                    <button
                      onClick={() => {
                        const newValue = !includeFiltered;
                        setIncludeFiltered(newValue);
                        // Use unified reload function
                        reloadInitialAvatars({ includeFilteredOverride: newValue });
                      }}
                      className={`relative inline-flex items-center rounded-full transition-colors duration-200 focus:outline-none ${
                        includeFiltered ? 'bg-indigo-600' : 'bg-gray-600'
                      }`}
                      style={{ height: '28.8px', width: '52.8px' }}
                    >
                      <span
                        className={`inline-block transform rounded-full bg-white transition-transform duration-200 ${
                          includeFiltered ? 'translate-x-7' : 'translate-x-1'
                        }`}
                        style={{ height: '19.2px', width: '19.2px' }}
                      />
                    </button>
                  </div>
                </div>

                {/* Tags Section */}
                <div className="space-y-3">
                  <h4 className="text-sm font-medium text-gray-300 font-['Montserrat',Helvetica]">{t('tags')}</h4>
                  <div className="flex flex-wrap gap-2">
                    {allTagsList.map(tag => (
                      <button
                        key={tag.tag_id}
                        onClick={() => handleTagFilterChange(tag.tag_id)}
                        className={`px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 font-['Montserrat',Helvetica] ${
                          selectedTags.includes(tag.tag_id)
                            ? "bg-[#ffffff1a] text-white shadow-[0px_0px_8px_#ffffff40] border border-white/80 outline outline-2 outline-white/80"
                            : "bg-[#2a2a2e] text-gray-300 hover:bg-[#3a3a3e] border border-transparent"
                        }`}
                      >
                        {tTags(tag.tag_name)}
                      </button>
                    ))}
                    {allTagsList.length === 0 && !isLoadingTags && (
                      <div className="text-gray-400 text-sm">{t('noTagsAvailable')}</div>
                    )}
                    {isLoadingTags && (
                      <div className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                        <span className="text-gray-400 text-sm">{t('loadingTags')}</span>
                      </div>
                    )}
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        )}

        {/* Scrollable Content Section - Grid layout */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden dark-scrollbar">
          <div className="flex flex-col gap-6 pb-6">
            {/* Character Grid */}
            <div className="px-0 w-full">
              <div
                ref={gridRef}
                className="grid w-full"
                style={{
                  gridTemplateColumns: `repeat(auto-fill, minmax(${MIN_CARD_WIDTH}px, 1fr))`,
                  gap: `clamp(${MIN_GAP}px, 1vw, ${MAX_GAP}px)`,
                  width: '100%',
                  maxWidth: '100%',
                  overflow: 'hidden'
                }}
              >
                {avatars.map((avatar, index) => {
                  const isLastElement = index === avatars.length - 1;
                  return (
                    <Link
                      key={avatar.metadata.avatar_id}
                      href={`/chat/${avatar.metadata.avatar_id}`}
                    >
                      <Card
                        ref={isLastElement ? lastAvatarElementRef : undefined}
                        className="discover-card relative rounded-[8px] overflow-hidden border-0 transition-transform duration-300 ease-in-out cursor-pointer hover:scale-[1.02]"
                        style={{
                          width: '100%',
                          aspectRatio: '9 / 16',
                          height: `${cardWidth * 16 / 9}px`,
                          maxWidth: '100%',
                          flexShrink: 0
                        }}
                      >
                        {avatar.metadata.presignedUrl ? (
                          <>
                            {cloudinaryImages[avatar.metadata.avatar_id] ? (
                              <AdvancedImage
                                cldImg={cloudinaryImages[avatar.metadata.avatar_id]}
                                alt={avatar.metadata.avatar_name}
                                className="object-cover w-full h-full pointer-events-none"
                              />
                            ) : (
                              <div className="w-full h-full bg-gray-700 rounded-[13.79px] animate-pulse" />
                            )}
                            {/* Character info overlay - positioned at the bottom */}
                            <div className="absolute bottom-0 w-full">
                              {/* Fade to black gradient overlay */}
                              <div className="absolute bottom-0 w-full h-20 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none"></div>
                              {/* Character text with proper padding */}
                              <div className="relative p-3 z-20">
                                <h3 className="w-full font-['Montserrat',Helvetica] font-semibold text-white text-[13.8px] leading-normal truncate mb-1">
                                  {avatar.metadata.avatar_name}
                                </h3>
                                <p className="self-stretch font-['Montserrat',Helvetica] font-normal text-neutral-300 text-xs leading-snug overflow-hidden [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:2]">
                                  {avatar.metadata.agent_bio || avatar.metadata.prompt}
                                </p>
                              </div>
                            </div>
                          </>
                        ) : (
                          <div className="w-full h-full bg-gray-700 rounded-[8px] animate-pulse" />
                        )}
                      </Card>
                    </Link>
                  );
                })}
              </div>
            </div>
            
            {/* Status messages outside of grid */}
            {/* Loading indicator */}
            {isLoading && (
              <div className="w-full flex justify-center py-4 mt-6">
                <div className="flex items-center gap-2">
                <Loader2 className="h-5 w-5 animate-spin text-white" />
                <span className="text-white">{t('loadingMoreCharacters')}</span>
                </div>
              </div>
            )}
            {searchStatus === SearchStatus.NoResults && (
              <div className="w-full text-center py-4 text-gray-400 mt-6">
                {t('noMatchingResult')}
              </div>
            )}
            {/* End of results message */}
            {!hasMore && !isLoading && avatars.length > 0 && (
              <div className="w-full text-center py-4 text-gray-400 mt-6">
                {t('foundCharacters', { count: avatars.length })}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* PWA Components */}
      <PWAInstallBanner />

      {/* Add CSS for slide-up animation and prevent horizontal scroll */}
      <style jsx>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        
        /* Prevent horizontal scrolling on mobile */
        body {
          overflow-x: hidden;
          width: 100%;
          position: relative;
        }
        
        /* Ensure the main container doesn't cause horizontal scroll */
        html {
          overflow-x: hidden;
        }
      `}</style>
    </Suspense>
  );
}